import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { normalizeProfileUserId } from '../utils/profileRoutes';
import { normalizePresenceStatus } from '../utils/presenceStatus';
import { registerUserScopedClientStateCleaner } from '../services/clientSessionLifecycle';

const PresenceContext = createContext(null);

const STATUS_CACHE_LIMIT = 1000;
const STATUS_MAX_AGE_MS = 30 * 60 * 1000;
const UNTRACKED_RETAIN_MS = 5 * 60 * 1000;
const PRUNE_INTERVAL_MS = 60 * 1000;

const normalizeUserId = (userId) => (
    typeof userId === 'string' ? normalizeProfileUserId(userId) : null
);

const normalizeUserIds = (userIds) => {
    if (!Array.isArray(userIds) || userIds.length === 0) {
        return [];
    }

    const normalized = new Set();
    userIds.forEach((userId) => {
        const nextUserId = normalizeUserId(userId);
        if (nextUserId) {
            normalized.add(nextUserId);
        }
    });
    return Array.from(normalized);
};

const isSameStatus = (current, next) => (
    Boolean(current)
    && current.userId === next.userId
    && current.online === next.online
    && (current.lastSeen || null) === (next.lastSeen || null)
    && (current.status || undefined) === (next.status || undefined)
    && (current.epoch ?? null) === (next.epoch ?? null)
);

export const PresenceProvider = ({ children }) => {
    const [statuses, setStatuses] = useState({});
    const trackedUsersRef = useRef(new Map());
    const statusMetadataRef = useRef(new Map());
    const acceptedEpochsRef = useRef(new Map());

    const rememberTrackedUsers = useCallback((userIds, now) => {
        userIds.forEach((userId) => {
            const current = trackedUsersRef.current.get(userId);
            trackedUsersRef.current.set(userId, {
                refCount: current?.refCount || 0,
                lastActiveAt: now,
            });
        });
    }, []);

    const isTrackedUser = useCallback((userId, now) => {
        const tracked = trackedUsersRef.current.get(userId);
        if (!tracked) {
            return false;
        }
        if ((tracked.refCount || 0) > 0) {
            return true;
        }
        return now - (tracked.lastActiveAt || 0) < UNTRACKED_RETAIN_MS;
    }, []);

    const prunePresenceState = useCallback((currentStatuses, now = Date.now()) => {
        trackedUsersRef.current.forEach((entry, userId) => {
            if ((entry.refCount || 0) <= 0 && now - (entry.lastActiveAt || 0) >= UNTRACKED_RETAIN_MS) {
                trackedUsersRef.current.delete(userId);
                statusMetadataRef.current.delete(userId);
                acceptedEpochsRef.current.delete(userId);
            }
        });

        let nextStatuses = currentStatuses;
        Object.keys(currentStatuses).forEach((userId) => {
            const metadata = statusMetadataRef.current.get(userId);
            const isExpired = metadata && now - metadata.updatedAt >= STATUS_MAX_AGE_MS;
            if (isExpired || !isTrackedUser(userId, now)) {
                if (nextStatuses === currentStatuses) {
                    nextStatuses = { ...currentStatuses };
                }
                delete nextStatuses[userId];
                statusMetadataRef.current.delete(userId);
                acceptedEpochsRef.current.delete(userId);
            }
        });

        let entries = Object.keys(nextStatuses);
        if (entries.length > STATUS_CACHE_LIMIT) {
            if (nextStatuses === currentStatuses) {
                nextStatuses = { ...currentStatuses };
            }
            entries
                .sort((left, right) => {
                    const leftUpdatedAt = statusMetadataRef.current.get(left)?.updatedAt || 0;
                    const rightUpdatedAt = statusMetadataRef.current.get(right)?.updatedAt || 0;
                    return leftUpdatedAt - rightUpdatedAt;
                })
                .slice(0, entries.length - STATUS_CACHE_LIMIT)
                .forEach((userId) => {
                    delete nextStatuses[userId];
                    statusMetadataRef.current.delete(userId);
                    acceptedEpochsRef.current.delete(userId);
                });
        }

        return nextStatuses;
    }, [isTrackedUser]);

    const applyStatuses = useCallback((entries, options = {}) => {
        const normalizedEntries = entries
            .map((entry) => normalizePresenceStatus(entry))
            .filter(Boolean);

        if (normalizedEntries.length === 0) {
            return;
        }

        const {
            allowUntracked = true,
            track = allowUntracked,
        } = options;
        const now = Date.now();

        if (track) {
            rememberTrackedUsers(normalizedEntries.map((entry) => entry.userId), now);
        }

        setStatuses((prev) => {
            let next = prev;
            let mutated = false;

            normalizedEntries.forEach((entry) => {
                if (!allowUntracked && !isTrackedUser(entry.userId, now)) {
                    return;
                }

                const current = next[entry.userId];
                const acceptedVersion = acceptedEpochsRef.current.get(entry.userId);
                const entryEpoch = entry.epoch ?? null;
                if (acceptedVersion && entryEpoch === null) {
                    return;
                }
                if (acceptedVersion && entryEpoch < acceptedVersion.epoch) {
                    return;
                }
                if (acceptedVersion
                    && entryEpoch === acceptedVersion.epoch
                    && !isSameStatus(acceptedVersion.status, entry)) {
                    return;
                }
                if (entryEpoch !== null
                    && (!acceptedVersion || entryEpoch > acceptedVersion.epoch)) {
                    acceptedEpochsRef.current.set(entry.userId, {
                        epoch: entryEpoch,
                        status: entry,
                    });
                }
                statusMetadataRef.current.set(entry.userId, { updatedAt: now });
                if (isSameStatus(current, entry)) {
                    return;
                }

                if (next === prev) {
                    next = { ...prev };
                }
                next[entry.userId] = {
                    ...current,
                    ...entry,
                };
                mutated = true;
            });

            const pruned = prunePresenceState(next, now);
            if (mutated || pruned !== prev) {
                return pruned;
            }
            return prev;
        });
    }, [isTrackedUser, prunePresenceState, rememberTrackedUsers]);

    const trackUsers = useCallback((userIds) => {
        const normalizedIds = normalizeUserIds(userIds);
        if (normalizedIds.length === 0) {
            return () => {};
        }

        const now = Date.now();
        normalizedIds.forEach((userId) => {
            const current = trackedUsersRef.current.get(userId);
            trackedUsersRef.current.set(userId, {
                refCount: (current?.refCount || 0) + 1,
                lastActiveAt: now,
            });
        });

        setStatuses((prev) => prunePresenceState(prev, now));

        let released = false;
        return () => {
            if (released) {
                return;
            }
            released = true;

            const releaseAt = Date.now();
            normalizedIds.forEach((userId) => {
                const current = trackedUsersRef.current.get(userId);
                if (!current) {
                    return;
                }
                trackedUsersRef.current.set(userId, {
                    refCount: Math.max(0, (current.refCount || 0) - 1),
                    lastActiveAt: releaseAt,
                });
            });
            setStatuses((prev) => prunePresenceState(prev, releaseAt));
        };
    }, [prunePresenceState]);

    const upsertStatus = useCallback((status, options) => {
        applyStatuses([status], options);
    }, [applyStatuses]);

    const upsertMany = useCallback((entries, options) => {
        if (!Array.isArray(entries) || entries.length === 0) {
            return;
        }
        applyStatuses(entries, options);
    }, [applyStatuses]);

    const clearStatus = useCallback((userId, options = {}) => {
        const normalizedUserId = normalizeUserId(userId);
        if (!normalizedUserId) {
            return;
        }

        const hasExpectedStatus = Object.prototype.hasOwnProperty.call(options, 'expectedStatus');
        const expectedStatus = options.expectedStatus || null;

        setStatuses((prev) => {
            const current = prev[normalizedUserId];
            if (!current) {
                statusMetadataRef.current.delete(normalizedUserId);
                return prev;
            }

            if (hasExpectedStatus) {
                if (!expectedStatus || !isSameStatus(current, expectedStatus)) {
                    return prev;
                }
            }

            const next = { ...prev };
            delete next[normalizedUserId];
            statusMetadataRef.current.delete(normalizedUserId);

            const currentEpoch = current.epoch ?? null;
            if (currentEpoch !== null) {
                const acceptedEpoch = acceptedEpochsRef.current.get(normalizedUserId);
                if (!acceptedEpoch || currentEpoch >= acceptedEpoch.epoch) {
                    acceptedEpochsRef.current.set(normalizedUserId, {
                        epoch: currentEpoch,
                        status: null,
                    });
                }
            }

            return next;
        });
    }, []);

    const clearPresence = useCallback(() => {
        trackedUsersRef.current.clear();
        statusMetadataRef.current.clear();
        acceptedEpochsRef.current.clear();
        setStatuses((prev) => (Object.keys(prev).length > 0 ? {} : prev));
    }, []);

    useEffect(
        () => registerUserScopedClientStateCleaner(clearPresence),
        [clearPresence],
    );

    const getStatus = useCallback((userId) => {
        const normalizedUserId = normalizeUserId(userId);
        if (!normalizedUserId) {
            return undefined;
        }
        return statuses[normalizedUserId];
    }, [statuses]);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.setInterval !== 'function') {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            const now = Date.now();
            setStatuses((prev) => prunePresenceState(prev, now));
        }, PRUNE_INTERVAL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [prunePresenceState]);

    const value = useMemo(() => ({
        statuses,
        upsertStatus,
        upsertMany,
        clearStatus,
        trackUsers,
        clearPresence,
        getStatus,
    }), [statuses, upsertStatus, upsertMany, clearStatus, trackUsers, clearPresence, getStatus]);

    return (
        <PresenceContext.Provider value={value}>
            {children}
        </PresenceContext.Provider>
    );
};

export const usePresence = () => {
    const context = useContext(PresenceContext);
    if (!context) {
        throw new Error('usePresence must be used within a PresenceProvider');
    }
    return context;
};

export const usePresenceOptional = () => useContext(PresenceContext);
